import type {
  Course,
  CourseModule,
  Section,
  VideoFile,
  VideoMetadata,
  User,
  CourseAccess,
  VideoSearchResult,
  PrimaryStyleRecord,
  ParamRecord,
  PermissionRecord,
} from '../types';

import { ApiError } from './error';

const rawApiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/api';
const isLocal =
  typeof location !== 'undefined' && ['localhost', '127.0.0.1'].includes(location.hostname);
const API_URL = (isLocal ? 'http://localhost:3000/api' : rawApiUrl).replace(/\/$/, '');
export const API_BASE_URL = API_URL.replace(/\/api\/?$/, '');

export { ApiError } from './error';

const TOKEN_KEY = 'dance.auth.token';

const getToken = () => (typeof localStorage === 'undefined' ? null : localStorage.getItem(TOKEN_KEY));
const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
const clearToken = () => {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(TOKEN_KEY);
};

let unauthorizedListener: (() => void) | null = null;
export const onUnauthorized = (listener: (() => void) | null) => {
  unauthorizedListener = listener;
};

const notifyUnauthorized = () => {
  clearToken();
  unauthorizedListener?.();
};

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    notifyUnauthorized();
    throw new ApiError(401, null, 'No autenticado');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(res.status, data, data.message ?? `HTTP ${res.status}`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isFormData = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: isFormData ? (body as FormData) : body === undefined ? undefined : JSON.stringify(body),
  });

  return handleResponse<T>(res);
}

function requestFormData<T>(
  method: string,
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (signal?.aborted) {
      reject(new ApiError(0, null, 'Subida cancelada'));
      return;
    }
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.open(method, `${API_URL}${path}`);
    xhr.withCredentials = true;
    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (onProgress) {
      xhr.upload.addEventListener('loadstart', () => onProgress(0));
      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      });
    }

    xhr.addEventListener('load', () => {
      const response = (() => {
        try {
          return JSON.parse(xhr.responseText) as unknown;
        } catch {
          return null;
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response as T);
      } else if (xhr.status === 401) {
        notifyUnauthorized();
        reject(new ApiError(401, response, 'No autenticado'));
      } else {
        const data = (response as { message?: string | string[] } | null) ?? null;
        const rawMessage = data?.message;
        const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage ?? `HTTP ${xhr.status}`;
        reject(new ApiError(xhr.status, data, message));
      }
    });

    xhr.addEventListener('error', () => reject(new ApiError(0, null, 'Error de red')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, null, 'Subida cancelada')));
    xhr.send(formData);
  });
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: (path: string) => request<unknown>('DELETE', path),

  login: async (email: string, password: string) => {
    const data = await request<{ id: string; email: string; role: string; token: string; permissions: string[] }>(
      'POST',
      '/auth/login',
      { email, password },
    );
    setToken(data.token);
    return data;
  },

  register: (data: {
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    password: string;
    role?: string;
  }) => request<User>('POST', '/auth/register', data),

  logout: async () => {
    try {
      return await request<{ ok: boolean }>('POST', '/auth/logout');
    } finally {
      clearToken();
    }
  },

  me: () => request<{ userId: string; email: string; role: string; permissions: string[] }>('GET', '/auth/me'),

  getDashboard: () => request<{ courses: number; users: number }>('GET', '/admin/dashboard'),

  getCourses: () => request<Course[]>('GET', '/courses'),
  getCourse: (courseId: string) => request<Course>('GET', `/courses/${courseId}`),
  getCourseProgress: (courseId: string) =>
    request<{ completedSectionIds: string[] }>('GET', `/courses/${courseId}/progress`),
  createCourse: (
    data: { name: string; description?: string },
    image?: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    if (image) formData.append('image', image);
    return requestFormData<Course>('POST', '/courses', formData, onProgress, signal);
  },
  updateCourse: (
    courseId: string,
    data: { name?: string; description?: string },
    image?: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData();
    if (data.name) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description ?? '');
    if (image) formData.append('image', image);
    return requestFormData<Course>('PATCH', `/courses/${courseId}`, formData, onProgress, signal);
  },
  deleteCourse: (courseId: string) => request<void>('DELETE', `/courses/${courseId}`),

  getModules: (courseId: string) => request<CourseModule[]>('GET', `/courses/${courseId}/modules`),
  createModule: (courseId: string, data: { title: string; description?: string; orderIndex?: number }) =>
    request<CourseModule>('POST', `/courses/${courseId}/modules`, data),

  getModule: (moduleId: string) => request<CourseModule>('GET', `/modules/${moduleId}`),
  updateModule: (moduleId: string, data: { title?: string; description?: string; orderIndex?: number }) =>
    request<CourseModule>('PATCH', `/modules/${moduleId}`, data),
  deleteModule: (moduleId: string) => request<void>('DELETE', `/modules/${moduleId}`),
  reorderModules: (courseId: string, orderedIds: string[]) =>
    request<CourseModule[]>('PATCH', `/courses/${courseId}/modules/reorder`, { orderedIds }),

  getSections: (moduleId: string) => request<Section[]>('GET', `/modules/${moduleId}/sections`),
  createSection: (moduleId: string, data: { title: string; description?: string; orderIndex?: number; markdownContent?: string }) =>
    request<Section>('POST', `/modules/${moduleId}/sections`, data),

  getSection: (sectionId: string) => request<Section>('GET', `/sections/${sectionId}`),
  getSectionMetadata: (sectionId: string) => request<VideoMetadata | null>('GET', `/sections/${sectionId}/metadata`),
  updateSection: (sectionId: string, data: { title?: string; description?: string; orderIndex?: number; markdownContent?: string }) =>
    request<Section>('PATCH', `/sections/${sectionId}`, data),
  deleteSection: (sectionId: string) => request<void>('DELETE', `/sections/${sectionId}`),
  reorderSections: (moduleId: string, orderedIds: string[]) =>
    request<Section[]>('PATCH', `/modules/${moduleId}/sections/reorder`, { orderedIds }),

  getVideoUrl: (videoFileId: string) => request<{ url: string }>('GET', `/video-files/${videoFileId}`),
  getVideoStreamUrl: async (videoFileId: string) => {
    const { ticket } = await request<{ ticket: string }>('GET', `/videos/${videoFileId}/stream-token`);
    return `${API_URL}/videos/${videoFileId}/stream?ticket=${encodeURIComponent(ticket)}`;
  },

  uploadVideo: (
    sectionId: string,
    file: File,
    metadata: Omit<VideoMetadata, 'id' | 'sectionId' | 'createdAt' | 'updatedAt'>,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('metadata', JSON.stringify(metadata));
    return requestFormData<{ videoFile: VideoFile; videoMetadata: VideoMetadata }>(
      'POST',
      `/sections/${sectionId}/videos`,
      formData,
      onProgress,
      signal,
    );
  },

  attachVideoLink: (
    sectionId: string,
    url: string,
    metadata: Omit<VideoMetadata, 'id' | 'sectionId' | 'createdAt' | 'updatedAt'>,
  ) =>
    request<{ videoFile: VideoFile; videoMetadata: VideoMetadata }>('POST', `/sections/${sectionId}/videos/link`, {
      url,
      metadata,
    }),

  getSectionProgress: (sectionId: string) => request<{ completed: boolean }>('GET', `/sections/${sectionId}/progress`),
  markSectionProgress: (sectionId: string) =>
    request<{ completed: boolean }>('POST', `/sections/${sectionId}/progress`),

  searchUsers: (q: string) => request<User[]>('GET', `/users?q=${encodeURIComponent(q)}`),

  getUser: (id: string) => request<User>('GET', `/users/${id}`),
  getUserAccesses: (id: string) => request<CourseAccess[]>('GET', `/users/${id}/accesses`),
  updateUserRole: (id: string, role: string) =>
    request<User>('PATCH', `/users/${id}/role`, { role }),
  changePassword: (id: string, currentPassword: string, newPassword: string) =>
    request<User>('PATCH', `/users/${id}/password`, { currentPassword, newPassword }),
  revokeAccess: (userId: string, courseId: string) =>
    request<void>('DELETE', `/users/${userId}/accesses/${courseId}`),

  grantAccess: (courseId: string, data: { userId: string; courseId: string; accessLevel?: string }) =>
    request<{ ok: boolean }>('POST', `/courses/${courseId}/access`, data),

  searchVideos: (params: { q?: string; style?: string; courseId?: string }) => {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.style) query.set('style', params.style);
    if (params.courseId) query.set('courseId', params.courseId);
    return request<{ results: VideoSearchResult[] }>('GET', `/videos/search?${query.toString()}`).then(
      (data) => data.results,
    );
  },
  getAdminLabels: (type: string, style?: string) =>
    request<{ labels: { id: string; name: string; styles: string[] }[] }>('GET', `/admin/labels?type=${type}${style ? `&style=${style}` : ''}`),
  createAdminLabel: (data: { type: string; name: string; styles: string[] }) =>
    request<{ label: { id: string; name: string; styles: string[] } }>('POST', '/admin/labels', data),
  deleteAdminLabel: (id: string) => request<void>('DELETE', `/admin/labels/${id}`),
  getPrimaryStyles: () => request<{ styles: PrimaryStyleRecord[] }>('GET', '/admin/primary-styles').then((data) => data.styles),
  createPrimaryStyle: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ style: PrimaryStyleRecord }>('POST', '/admin/primary-styles', data),
  updatePrimaryStyle: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ style: PrimaryStyleRecord }>('PATCH', `/admin/primary-styles/${encodeURIComponent(value)}`, data),
  deletePrimaryStyle: (value: string) => request<void>('DELETE', `/admin/primary-styles/${encodeURIComponent(value)}`),
  getDifficulties: () => request<{ difficulties: ParamRecord[] }>('GET', '/admin/difficulties').then((data) => data.difficulties),
  createDifficulty: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ difficulty: ParamRecord }>('POST', '/admin/difficulties', data),
  updateDifficulty: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ difficulty: ParamRecord }>('PATCH', `/admin/difficulties/${encodeURIComponent(value)}`, data),
  deleteDifficulty: (value: string) => request<void>('DELETE', `/admin/difficulties/${encodeURIComponent(value)}`),
  getVideoTypes: () => request<{ videoTypes: ParamRecord[] }>('GET', '/admin/video-types').then((data) => data.videoTypes),
  createVideoType: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ videoType: ParamRecord }>('POST', '/admin/video-types', data),
  updateVideoType: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ videoType: ParamRecord }>('PATCH', `/admin/video-types/${encodeURIComponent(value)}`, data),
  deleteVideoType: (value: string) => request<void>('DELETE', `/admin/video-types/${encodeURIComponent(value)}`),
  getLabelTypes: () => request<{ labelTypes: ParamRecord[] }>('GET', '/admin/label-types').then((data) => data.labelTypes),
  createLabelType: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ labelType: ParamRecord }>('POST', '/admin/label-types', data),
  updateLabelType: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ labelType: ParamRecord }>('PATCH', `/admin/label-types/${encodeURIComponent(value)}`, data),
  deleteLabelType: (value: string) => request<void>('DELETE', `/admin/label-types/${encodeURIComponent(value)}`),
  getAccessLevels: () => request<{ accessLevels: ParamRecord[] }>('GET', '/admin/access-levels').then((data) => data.accessLevels),
  createAccessLevel: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ accessLevel: ParamRecord }>('POST', '/admin/access-levels', data),
  updateAccessLevel: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ accessLevel: ParamRecord }>('PATCH', `/admin/access-levels/${encodeURIComponent(value)}`, data),
  deleteAccessLevel: (value: string) => request<void>('DELETE', `/admin/access-levels/${encodeURIComponent(value)}`),
  getPermissions: () =>
    request<{ permissions: PermissionRecord[] }>('GET', '/admin/permissions').then((data) => data.permissions),
  getRolePermissions: (roleValue: string) =>
    request<{ permissions: string[]; isSuperuser: boolean }>(
      'GET',
      `/admin/roles/${encodeURIComponent(roleValue)}/permissions`,
    ),
  setRolePermissions: (roleValue: string, permissions: string[], isSuperuser: boolean) =>
    request<{ permissions: string[]; isSuperuser: boolean }>(
      'PUT',
      `/admin/roles/${encodeURIComponent(roleValue)}/permissions`,
      { permissions, isSuperuser },
    ),
  getRoles: () => request<{ roles: ParamRecord[] }>('GET', '/admin/roles').then((data) => data.roles),
  createRole: (data: { value: string; label: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ role: ParamRecord }>('POST', '/admin/roles', data),
  updateRole: (value: string, data: { label?: string; orderIndex?: number; isActive?: boolean }) =>
    request<{ role: ParamRecord }>('PATCH', `/admin/roles/${encodeURIComponent(value)}`, data),
  deleteRole: (value: string) => request<void>('DELETE', `/admin/roles/${encodeURIComponent(value)}`),
  getVideoLabels: (type: string, q?: string, style?: string) => {
    const query = new URLSearchParams();
    query.set('type', type);
    if (q) query.set('q', q);
    if (style) query.set('style', style);
    return request<{ labels: string[] }>('GET', `/videos/labels?${query.toString()}`).then((data) => data.labels);
  },

  getParams: () =>
    request<{
      primaryStyles: ParamRecord[];
      difficulties: ParamRecord[];
      videoTypes: ParamRecord[];
      labelTypes: ParamRecord[];
      accessLevels: ParamRecord[];
      roles: ParamRecord[];
    }>('GET', '/params'),
};
